import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WorkCard from "@/components/feature/WorkCard";
import { works } from "@/mocks/works";

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  if (!work) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center pt-24">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-error-warning-line" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 text-sm text-foreground-600">作品可能已下架或链接有误</p>
          <Link to="/works" className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600">
            返回作品库
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: Math.min(work.chapters, 20) }, (_, i) => work.chapters - i);
  const related = works.filter((w) => w.category === work.category && w.id !== work.id).slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="bg-background-50">
        <section className="relative overflow-hidden bg-background-100 pt-24 md:pt-28">
          <div className="absolute -right-20 top-0 h-64 w-64 rounded-full bg-primary-100/70 blur-3xl" />
          <div className="absolute -left-16 bottom-0 h-52 w-52 rounded-full bg-accent-100/70 blur-3xl" />
          <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
                <li className="flex items-center gap-1.5"><Link to="/" className="hover:text-primary-600">首页</Link><i className="ri-arrow-right-s-line" /></li>
                <li className="flex items-center gap-1.5"><Link to="/works" className="hover:text-primary-600">作品库</Link><i className="ri-arrow-right-s-line" /></li>
                <li className="text-foreground-700">{work.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-10">
              <div className="mx-auto w-44 shrink-0 md:mx-0 md:w-64">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-sm">
                  <img
                    src={work.cover}
                    alt={`${work.title} 封面`}
                    title={`${work.title} ${work.status} ${work.chapters}话`}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute left-2 top-2 flex flex-col gap-1.5">
                    {work.isExclusive && (
                      <span className="rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-bold text-background-50">独家首发</span>
                    )}
                    {work.isNew && (
                      <span className="rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-background-50">新作上架</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
                  <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{work.title}</h1>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${work.status === "已完结" ? "bg-background-200 text-foreground-700" : "bg-accent-100 text-accent-800"}`}>
                    {work.status}
                  </span>
                </div>
                <p className="mt-3 text-sm text-foreground-600 md:text-base">
                  <b className="text-foreground-800">{work.author}</b> 著 · {work.category}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-sm md:justify-start">
                  <span className="flex items-center gap-1 text-secondary-600">
                    <i className="ri-star-fill" /> <b className="text-foreground-900">{work.rating}</b> 分
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-book-open-line" /> {work.chapters} 话
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-eye-line" /> {work.views} 人气
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-time-line" /> 更新于 {work.updateTime}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
                  {work.tags.map((t) => (
                    <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">{t}</span>
                  ))}
                </div>
                <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:mx-0 md:text-base">
                  {work.description}
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3 md:justify-start">
                  <button type="button" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">
                    <i className="ri-play-circle-line" /> 开始阅读
                  </button>
                  <button type="button" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 border border-background-300 px-7 py-3 text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-700">
                    <i className="ri-bookmark-line" /> 加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16" aria-label="目录">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-6 w-1 rounded-full bg-primary-500" />
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">章节目录</h2>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              共 {work.chapters} 话 · 最近 {chapters.length} 话
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((n) => (
              <button
                key={n}
                type="button"
                className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="text-sm text-foreground-700 group-hover:text-primary-700">
                  <i className="ri-file-text-line mr-2 text-primary-400" />
                  {n === work.chapters ? work.latestChapter : `第${n}话`}
                </span>
                <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500" />
              </button>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16" aria-label="相关推荐">
            <div className="mx-auto max-w-7xl px-4 md:px-6">
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-6 w-1 rounded-full bg-primary-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">同类型推荐</h2>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                {related.map((r) => (
                  <WorkCard key={r.id} work={r} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Book",
            name: work.title,
            author: { "@type": "Person", name: work.author },
            genre: work.category,
            inLanguage: "zh-CN",
            url: `https://fangupdate.github.io/work/${work.id}`,
            description: work.description,
            aggregateRating: { "@type": "AggregateRating", ratingValue: work.rating, ratingCount: "1286" },
            isPartOf: { "@type": "WebSite", name: "叮咚漫画网", url: "https://fangupdate.github.io/" },
          }),
        }}
      />
    </>
  );
}